import { ref } from 'vue'
import { App } from '@capacitor/app'
import { BlueIDAccess } from '@blueid/access-capacitor'

export const nearbyDevices = ref([])
export const isScanning = ref(false)
export const isAppForeground = ref(true)

let isNativeScanning = false
let lastScanStartTime = 0
let retryTimer = null
let registeredListeners = []
let appStateListener = null
let hasInitialized = false

const eventNames = [
  'DeviceAdded',
  'DeviceUpdated',
  'DeviceNearByDetected',
  'DeviceNearByUpdated',
  'deviceAdded',
  'deviceUpdated',
  'deviceNearByDetected',
  'deviceNearByUpdated',
]

const removeEventNames = [
  'DeviceRemoved',
  'DeviceNearByLost',
  'deviceRemoved',
  'deviceNearByLost',
]

export const extractDeviceId = info => {
  if (!info) return null
  if (typeof info === 'string') {
    const trimmed = info.trim()
    return trimmed.length >= 6 && !/^\d+$/.test(trimmed) && !/^[0-9a-f]{24}$/i.test(trimmed) ? trimmed : null
  }
  const candidates = [
    info?.deviceId,
    info?.blueDeviceId,
    info?.device?.deviceId,
    info?.device?.blueDeviceId,
    info?.data?.deviceId,
    info?.hardwareId,
    info?.macAddress,
    info?.id,
    info?._id,
    info?.device?.id,
    info?.data?.id,
    info?.uuid,
    info?.identifier,
    info?.value,
  ]
  for (const c of candidates) {
    if (c !== undefined && c !== null) {
      const str = String(c).trim()
      if (str.length >= 6 && !/^\d+$/.test(str) && !/^[0-9a-f]{24}$/i.test(str)) {
        return str
      }
    }
  }
  return null
}

const upsertDevice = info => {
  const id = extractDeviceId(info)
  if (!id) return
  const entry = { ...(typeof info === 'object' ? info : {}), deviceId: id, lastSeen: Date.now() }
  const index = nearbyDevices.value.findIndex(
    d => String(d.deviceId).toLowerCase() === id.toLowerCase()
  )
  if (index === -1) {
    nearbyDevices.value.push(entry)
  } else {
    Object.assign(nearbyDevices.value[index], entry)
  }
  console.log(`[BleScanner] Discovered/Updated nearby peripheral: ${id} (total nearby: ${nearbyDevices.value.length})`)
}

const removeDevice = info => {
  const id = extractDeviceId(info)
  if (!id) return
  const needle = id.toLowerCase()
  nearbyDevices.value = nearbyDevices.value.filter(
    d => String(d.deviceId).toLowerCase() !== needle
  )
  console.log(`[BleScanner] Peripheral left range: ${id} (remaining nearby: ${nearbyDevices.value.length})`)
}

const ensureListeners = async () => {
  if (registeredListeners.length > 0) return
  try {
    const addHandles = await Promise.all(
      eventNames.map(name => BlueIDAccess.addListener(name, upsertDevice).catch(() => null))
    )
    const removeHandles = await Promise.all(
      removeEventNames.map(name => BlueIDAccess.addListener(name, removeDevice).catch(() => null))
    )
    registeredListeners = [...addHandles, ...removeHandles].filter(Boolean)
  } catch (e) {
    console.warn('[BleScanner] Could not attach BLE event listeners:', e)
  }
}

const stopListeners = async () => {
  const previous = registeredListeners
  registeredListeners = []
  await Promise.all(previous.map(handle => Promise.resolve(handle?.remove?.()).catch(() => null)))
}

export const startBleScanning = async () => {
  if (!isAppForeground.value) {
    console.log('[BleScanner] App is in background - skipping scan start')
    return
  }

  await ensureListeners()

  if (isNativeScanning) {
    console.log('[BleScanner] Native scanning already active - keeping continuous foreground scan running')
    return
  }

  const now = Date.now()
  if (now - lastScanStartTime < 2000) {
    console.log('[BleScanner] Scan start throttled (minimum 2s cooldown to protect Android scan registration)')
    return
  }

  lastScanStartTime = now
  isNativeScanning = true
  isScanning.value = true

  try {
    await BlueIDAccess.runCommand('startScanning')
    console.log('[BleScanner] Continuous foreground BLE scanning active')
  } catch (err) {
    console.warn('[BleScanner] startScanning error:', err)
    isNativeScanning = false
    isScanning.value = false
    if (String(err).includes('frequently') || String(err).includes('status=6')) {
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = setTimeout(() => {
        if (isAppForeground.value) {
          console.log('[BleScanner] Retrying startScanning after rate-limit backoff...')
          void startBleScanning()
        }
      }, 3500)
    }
  }
}

export const stopBleScanning = async () => {
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }

  if (!isNativeScanning) {
    isScanning.value = false
    return
  }

  isNativeScanning = false
  isScanning.value = false

  try {
    await BlueIDAccess.runCommand('stopScanning')
    console.log('[BleScanner] Continuous BLE scanning stopped')
  } catch (err) {
    console.warn('[BleScanner] stopScanning warning:', err)
  }

  await stopListeners()
  nearbyDevices.value = []
}

export const initGlobalScanner = async () => {
  if (hasInitialized) return
  hasInitialized = true

  console.log('[BleScanner] Initializing app-wide foreground continuous BLE scanner')

  try {
    appStateListener = await App.addListener('appStateChange', async ({ isActive }) => {
      console.log(`[BleScanner] App state changed -> isActive=${isActive}`)
      if (isActive) {
        isAppForeground.value = true
        await startBleScanning()
      } else {
        isAppForeground.value = false
        await stopBleScanning()
      }
    })
  } catch (err) {
    console.warn('[BleScanner] Could not attach appStateChange listener:', err)
  }

  // Kick off scanning immediately in foreground
  await startBleScanning()
}

export const findNearbyDeviceId = (deviceIds, maxAgeMs = 45000) => {
  const list = (Array.isArray(deviceIds) ? deviceIds : [deviceIds])
    .map(id => String(id || '').trim().toLowerCase())
    .filter(id => id.length >= 6 && !/^\d+$/.test(id) && !/^[0-9a-f]{24}$/i.test(id))

  if (!list.length || !nearbyDevices.value.length) return null

  const now = Date.now()

  // 1. Exact match
  for (const id of list) {
    const match = nearbyDevices.value.find(d => {
      const devId = String(d?.deviceId || '').trim().toLowerCase()
      if (devId !== id) return false
      if (d?.lastSeen && now - d.lastSeen > maxAgeMs) return false
      return true
    })
    if (match) return match.deviceId
  }

  // 2. Prefix match
  for (const id of list) {
    const match = nearbyDevices.value.find(d => {
      const devId = String(d?.deviceId || '').trim().toLowerCase()
      if (devId.length < 6) return false
      if (!devId.startsWith(id) && !id.startsWith(devId)) return false
      if (d?.lastSeen && now - d.lastSeen > maxAgeMs) return false
      return true
    })
    if (match) return match.deviceId
  }

  return null
}

export const isDeviceNearby = (deviceIds, maxAgeMs = 45000) => {
  return Boolean(findNearbyDeviceId(deviceIds, maxAgeMs))
}

export const _resetScannerForTests = () => {
  hasInitialized = false
  isNativeScanning = false
  lastScanStartTime = 0
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
  registeredListeners = []
  appStateListener = null
  nearbyDevices.value = []
  isScanning.value = false
  isAppForeground.value = true
}
